import { ApiClient } from '../../ApiClient';
import { FullSyncJob } from '@domain/connection/models/ImportJob';
import { ShopifySettings, ShopifyProductMatchMapping } from '@domain/connection/models/ConnectionSettings';
import {
  CompleteShopifySetupResponse,
  FinishShopifySetupInput,
  ShopifyMatchPreviewResponse,
} from '@domain/connection/models/ShopifyMatchPreview';

export class ShopifyConnectorDataSource {

  constructor(private api: ApiClient) {
  }

  async initiateShopifyAuth(shop: string, redirectUri?: string): Promise<{ authUrl: string }> {
    const response = await this.api.post<{ authUrl: string }>('/connections/shopify/initiate-auth', {
      shop,
      redirectUri,
    });
    return response.data;
  }

  async reconnect(connectionId: string, redirectUri?: string): Promise<{ authUrl: string }> {
    const response = await this.api.post<{ authUrl: string }>(`/connections/shopify/${connectionId}/reconnect`, {
      redirectUri,
    });
    return response.data;
  }

  async initiateInstall(redirectUri?: string): Promise<{ installUrl: string }> {
    const response = await this.api.post<{ installUrl: string }>('/connections/shopify/initiate-install', {
      redirectUri,
    });
    return response.data;
  }

  async claimShopifyInstall(shop: string, installToken: string): Promise<{ connectionId: string }> {
    const response = await this.api.post<{ connectionId: string }>('/connections/shopify/claim-install', {
      shop,
      installToken,
    });
    return response.data;
  }

  async syncFull(connectionId: string): Promise<FullSyncJob[]> {
    const response = await this.api.post<{ jobs: FullSyncJob[] }>(`/connections/shopify/sync/${connectionId}`);
    return response.data.jobs;
  }

  async syncProducts(connectionId: string): Promise<void> {
    await this.api.post(`/connections/shopify/sync/${connectionId}/products`);
  }

  async syncOrders(connectionId: string): Promise<void> {
    await this.api.post(`/connections/shopify/sync/${connectionId}/orders`);
  }

  async syncInventory(connectionId: string): Promise<void> {
    await this.api.post(`/connections/shopify/sync/${connectionId}/inventory`);
  }

  async getSettings(connectionId: string): Promise<ShopifySettings> {
    const response = await this.api.get<ShopifySettings>(`/connections/${connectionId}/settings`);
    return response.data;
  }

  async updateSettings(connectionId: string, settings: Partial<ShopifySettings>): Promise<ShopifySettings> {
    const response = await this.api.put<ShopifySettings>(`/connections/${connectionId}/settings`, settings);
    return response.data;
  }

  async getMatchPreview(
    connectionId: string,
    mapping?: Partial<ShopifyProductMatchMapping>,
    pageToken?: string,
  ): Promise<ShopifyMatchPreviewResponse> {
    const response = await this.api.post<ShopifyMatchPreviewResponse>(
      `/connections/shopify/${connectionId}/match-preview`,
      {
        sourceField: mapping?.sourceField,
        targetField: mapping?.targetField,
        pageToken,
      },
    );
    return response.data;
  }

  async completeSetup(
    connectionId: string,
    productMatchMapping: ShopifyProductMatchMapping,
  ): Promise<CompleteShopifySetupResponse> {
    const response = await this.api.post<CompleteShopifySetupResponse>(
      `/connections/shopify/${connectionId}/complete-setup`,
      { productMatchMapping },
    );
    return response.data;
  }

  async finishSetup(
    connectionId: string,
    input: FinishShopifySetupInput,
  ): Promise<CompleteShopifySetupResponse> {
    const response = await this.api.post<CompleteShopifySetupResponse>(
      `/connections/shopify/${connectionId}/finish-setup`,
      input,
    );
    return response.data;
  }
}
